import * as i0 from '@angular/core';
import { Directive, INJECTOR, forwardRef, Component, Inject, Input, ViewChild, NgModule } from '@angular/core';
import * as i11 from '@angular/forms';
import { FormGroup, FormControl, NgControl, Validators, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import * as i4 from '@angular/material/form-field';
import { MatFormFieldModule } from '@angular/material/form-field';
import { map, tap, take, distinctUntilChanged, filter, takeUntil, switchMap } from 'rxjs/operators';
import { FFValidators, nextOnChange, randomString, updateValueAndValidityRecursively, deepEquals, FFObject, isNullOrEmpty } from '@ff/utils';
import { Subject, ReplaySubject, BehaviorSubject, combineLatest } from 'rxjs';
import * as i1 from '@ff/localization';
import { LocalizationModule } from '@ff/localization';
import * as i2 from '@angular/material/core';
import * as i3 from '@ff/material-controls/core';
import { DateTime } from 'luxon';
import '@ff/material-controls/core/interfaces';
import * as i5 from '@angular/material/datepicker';
import { MatDatepickerModule } from '@angular/material/datepicker';
import * as i6 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i7 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i8 from '@angular/material/progress-spinner';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import * as i9 from '@ff/material-controls/localized-mat-error';
import { FFLocalizedMatErrorModule } from '@ff/material-controls/localized-mat-error';
import * as i10 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as textMask from 'vanilla-text-mask/dist/vanillaTextMask';
import * as i13 from '@angular/material/input';
import { MatInputModule } from '@angular/material/input';
import { FFForceUppercaseModule, FFRegexFilterModule } from '@ff/material-controls/core/directives';

class FFMaskDateDirective {
    constructor(element) {
        this.element = element;
        this.mask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]; // dd/mm/yyyy
        this.maskedInputController = textMask.maskInput({
            inputElement: this.element.nativeElement,
            mask: this.mask
        });
    }
    ngOnDestroy() {
        // @ts-ignore
        this.maskedInputController.destroy();
    }
}
FFMaskDateDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFMaskDateDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FFMaskDateDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FFMaskDateDirective, selector: "[ffMaskDate]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFMaskDateDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[ffMaskDate]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

class FFDatePickerComponent {
    constructor(injector, _languageQuery, _adapter, _configService) {
        this.injector = injector;
        this._languageQuery = _languageQuery;
        this._adapter = _adapter;
        this._configService = _configService;
        this._unsubscribe$ = new Subject();
        this._fromDateLabel$$ = new ReplaySubject(1);
        this._toDateLabel$$ = new ReplaySubject(1);
        this._controlType$$ = new BehaviorSubject('single');
        this._dateRangeValue$$ = new BehaviorSubject(null);
        this._singleDateValue$$ = new BehaviorSubject(null);
        this._dateLengthWithMask = 10;
        this._startDate$$ = new ReplaySubject(1);
        this._isDisabled$$ = new ReplaySubject(1);
        this._updateRangeValueAndValidity$ = new ReplaySubject(1);
        this._isValidatorsSet = false;
        this.startDate$$ = new ReplaySubject(1);
        this.isRequired$$ = new BehaviorSubject(false);
        this.singleLabel = null;
        this.fromDateLabel = null;
        this.toDateLabel = null;
        this.fromDateName = null;
        this.toDateName = null;
        this.singleName = null;
        this.className = null;
        this.loaderDiameter = 20;
        this.tabIndex = 100;
        this.clearable = false;
        this.hideRequiredMarker = false;
        this.controlType = 'single';
        this.customErrorMessages = [];
        this.startDate = null;
        this.startView = 'month';
        this.isShowLoading = false;
        this.dataType = null;
        this.labelDateRange$$ = combineLatest([this._fromDateLabel$$, this._toDateLabel$$]).pipe(map(([fromDateLabel, toDateLabel]) => {
            const isRange = fromDateLabel != null && toDateLabel != null;
            const separator = isRange ? ' - ' : '';
            return `${fromDateLabel}${separator}${toDateLabel}`;
        }));
        this.fromDateControlName = '';
        this.toDateControlName = '';
        this.singleControlName = '';
        this.rangeFormGroup = new FormGroup({
            dateFrom: new FormControl(null),
            dateTo: new FormControl(null)
        });
        this.singleDateFormControl = new FormControl(null);
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.onTouched = () => {
        };
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.onChange = () => {
        };
        // eslint-disable-next-line @typescript-eslint/typedef
        this.datepickerFilter = (date) => {
            const dateTypes = ['dateOfBirth'];
            if (this.dataType == null || !(dateTypes.includes(this.dataType)) || date == null) {
                return true;
            }
            const minDate = DateTime.now().minus({ years: this.minAge });
            const maxDate = DateTime.now().minus({ years: 100 });
            return FFValidators.isDateNotLaterThan(date, minDate) && FFValidators.isDateNotEarlierThan(date, maxDate);
        };
        this.floatLabel = this._configService.getInputFloatLabel();
        this.appearance = this._configService.getInputAppearance();
        this.fromDateName = this.fromDateLabel;
        this.fromDateControlName = `ff-input-${this.fromDateName}`;
        this.toDateName = this.toDateLabel;
        this.toDateControlName = `ff-input-${this.toDateName}`;
        this.singleName = this.singleLabel;
        this.singleControlName = `ff-input-${this.singleName}`;
    }
    ngOnChanges(changes) {
        nextOnChange(changes.fromDateLabel, this._fromDateLabel$$);
        nextOnChange(changes.toDateLabel, this._toDateLabel$$);
        nextOnChange(changes.controlType, this._controlType$$);
        nextOnChange(changes.startDate, this._startDate$$);
    }
    ngDoCheck() {
        var _a, _b, _c;
        // Show errors immediately instead of waiting blur
        if (this._ngControl != null && this._ngControl.control != null && this._ngControl.control.touched) {
            this.singleDateFormControl.markAsTouched();
            this.singleDateFormControl.updateValueAndValidity({ emitEvent: false });
            this.rangeFormGroup.markAllAsTouched();
            this.rangeFormGroup.updateValueAndValidity({ emitEvent: false });
        }
        if (this.rangeFormGroup.touched) {
            this._updateRangeValueAndValidity$.next(randomString());
        }
        if (this.controlType === 'single' && !this._isValidatorsSet && ((_a = this._ngControl) === null || _a === void 0 ? void 0 : _a.control) != null && ((_c = (_b = this._ngControl) === null || _b === void 0 ? void 0 : _b.control) === null || _c === void 0 ? void 0 : _c.validator) != null) {
            this._isValidatorsSet = true;
            const existingValidator = this.singleDateFormControl.validator;
            this.singleDateFormControl.clearValidators();
            if (existingValidator != null) {
                this.singleDateFormControl.setValidators([this._ngControl.control.validator, existingValidator]);
            }
            else {
                this.singleDateFormControl.setValidators(this._ngControl.control.validator);
            }
        }
    }
    ngOnInit() {
        if (this.dataType != null && this.dataType === 'dateOfBirth' && this.minAge == null) {
            console.error(`Input parameter 'minAge' couldn't be null if 'dataType' equal 'dateOfBirth'`);
        }
        if (this.dataType != null && this.dataType === 'dateOfBirth' && this.minAge != null) {
            this._singleDateValue$$.pipe(tap((singleDateValue) => {
                this.startDate$$.next(singleDateValue == null || this.singleDateFormControl.invalid ? DateTime.now().minus({ year: this.minAge }) : singleDateValue);
            })).subscribe();
        }
        else {
            combineLatest([this._startDate$$, this._singleDateValue$$]).pipe(tap(([startDate, singleDateValue]) => {
                this.startDate$$.next(singleDateValue == null || this.singleDateFormControl.invalid || this.singleDateFormControl.value == null ? DateTime.fromJSDate(startDate) : singleDateValue);
            })).subscribe();
        }
        this._languageQuery.selectActive().pipe(take(1), tap((active) => {
            var _a;
            this._adapter.setLocale((_a = active === null || active === void 0 ? void 0 : active.id) !== null && _a !== void 0 ? _a : 'en-GB');
        })).subscribe();
        this._isDisabled$$.pipe(tap(isDisabled => {
            if (isDisabled) {
                this.singleDateFormControl.disable({ emitEvent: false });
                this.rangeFormGroup.disable({ emitEvent: false });
            }
            else {
                this.singleDateFormControl.enable({ emitEvent: false });
                this.rangeFormGroup.enable({ emitEvent: false });
            }
        })).subscribe();
    }
    ngAfterViewInit() {
        var _a, _b, _c, _d;
        this._ngControl = this.injector.get(NgControl);
        if (this._ngControl != null) {
            this.parentControl = this._ngControl.control;
            const isRequired = this.parentControl.hasValidator(Validators.required) || this.parentControl.hasValidator(FFValidators.requiredNoWhitespaces);
            const isDisabled = this.parentControl.disabled;
            this.isRequired$$.next(isRequired && !isDisabled);
            if (this.controlType === 'range' && isRequired) {
                (_a = this.rangeFormGroup.controls.dateFrom) === null || _a === void 0 ? void 0 : _a.addValidators(this._dateRangeRequired(this._rangeStartDate, this._rangeEndDate));
                (_b = this.rangeFormGroup.controls.dateTo) === null || _b === void 0 ? void 0 : _b.addValidators(this._dateRangeRequired(this._rangeStartDate, this._rangeEndDate));
                updateValueAndValidityRecursively(this.rangeFormGroup);
            }
        }
        (_c = this.singleDateFormControl) === null || _c === void 0 ? void 0 : _c.valueChanges.pipe(distinctUntilChanged((x, y) => deepEquals(x, y)), filter((date) => {
            const datePrev = this._singleDateValue$$.getValue();
            if (date == null || date.isValid === false) {
                return false;
            }
            return (date === null || date === void 0 ? void 0 : date.toUTC().toISO()) !== (datePrev === null || datePrev === void 0 ? void 0 : datePrev.toUTC().toISO());
        }), tap(value => {
            this._singleDateValue$$.next(value);
            this.handleChange(value);
        }), takeUntil(this._unsubscribe$)).subscribe();
        (_d = this.rangeFormGroup) === null || _d === void 0 ? void 0 : _d.valueChanges.pipe(filter((date) => {
            var _a, _b;
            const fromDatePrev = (_a = this._dateRangeValue$$.value) === null || _a === void 0 ? void 0 : _a.dateFrom;
            const toDatePrev = (_b = this._dateRangeValue$$.value) === null || _b === void 0 ? void 0 : _b.dateTo;
            if ((date === null || date === void 0 ? void 0 : date.dateFrom) == null) {
                return false;
            }
            return (date === null || date === void 0 ? void 0 : date.dateFrom) !== fromDatePrev || (date === null || date === void 0 ? void 0 : date.dateTo) !== toDatePrev;
        }), tap(value => {
            this._dateRangeValue$$.next(value);
            this.handleChange(value);
        }), takeUntil(this._unsubscribe$)).subscribe();
        this._updateRangeValueAndValidity$.pipe(switchMap(() => { var _a; return (_a = this.rangeFormGroup) === null || _a === void 0 ? void 0 : _a.statusChanges; }), filter((status) => status.toLowerCase() === 'invalid'), tap(() => {
            const dateFrom = this.rangeFormGroup.controls.dateFrom;
            const dateTo = this.rangeFormGroup.controls.dateTo;
            const errorsMap = new Map();
            if (dateFrom.errors != null) {
                FFObject.keys(dateFrom.errors).forEach(key => {
                    // @ts-ignore we checked it before
                    errorsMap.set(key, dateFrom.errors[key]);
                });
            }
            if (dateTo.errors != null) {
                FFObject.keys(dateTo.errors).forEach(key => {
                    // @ts-ignore we checked it before
                    errorsMap.set(key, dateTo.errors[key]);
                });
            }
            this.parentControl.setErrors(Object.fromEntries(errorsMap.entries()));
        }), takeUntil(this._unsubscribe$)).subscribe();
    }
    onRangeStartChange() {
        var _a;
        (_a = this.rangeFormGroup.controls.dateTo) === null || _a === void 0 ? void 0 : _a.updateValueAndValidity({ emitEvent: false });
    }
    onRangeEndChange() {
        var _a;
        (_a = this.rangeFormGroup.controls.dateFrom) === null || _a === void 0 ? void 0 : _a.updateValueAndValidity({ emitEvent: false });
    }
    ngOnDestroy() {
        this._unsubscribe$.next();
        this._unsubscribe$.complete();
    }
    inputChange(event) {
        if (event.target.value.replace(/_/g, '').length >= this._dateLengthWithMask) {
            this.onTouched();
        }
    }
    clearValue() {
        this.singleDateFormControl.reset();
        this.rangeFormGroup.reset();
    }
    writeValue(date) {
        var _a, _b;
        if (typeof date === 'string' && !isNullOrEmpty(date)) {
            if (this._controlType$$.getValue() === 'single' && date !== this.singleDateFormControl.value) {
                const singleDate = DateTime.fromISO(date);
                this._singleDateValue$$.next(singleDate);
                this.singleDateFormControl.patchValue(singleDate, { emitEvent: false });
            }
        }
        else if (date != null) {
            if (this._controlType$$.getValue() === 'range') {
                const formValue = this.rangeFormGroup.value;
                const dateRange = date;
                const isSamePeriod = (dateRange === null || dateRange === void 0 ? void 0 : dateRange.dateFrom) === ((_a = formValue.fromDate) === null || _a === void 0 ? void 0 : _a.toISODate()) &&
                    (dateRange === null || dateRange === void 0 ? void 0 : dateRange.dateTo) === ((_b = formValue.toDate) === null || _b === void 0 ? void 0 : _b.toISODate());
                if ((dateRange.dateFrom != null || dateRange.dateTo != null) && !isSamePeriod) {
                    this._dateRangeValue$$.next(date);
                    this.rangeFormGroup.patchValue(date, { emitEvent: false });
                }
            }
        }
    }
    handleChange(value) {
        var _a, _b, _c, _d;
        this.onTouched();
        if (this._controlType$$.getValue() === 'single') {
            const dateTimeValue = value;
            const parsedDate = dateTimeValue.isValid ? dateTimeValue.toUTC().toISO() : '';
            this.onChange(parsedDate);
        }
        else {
            const range = value;
            const parsedDateRange = {
                dateFrom: (_b = (_a = range.dateFrom) === null || _a === void 0 ? void 0 : _a.toUTC().toISO()) !== null && _b !== void 0 ? _b : null,
                dateTo: (_d = (_c = range.dateTo) === null || _c === void 0 ? void 0 : _c.toUTC().toISO()) !== null && _d !== void 0 ? _d : null
            };
            this.onChange(parsedDateRange);
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this._isDisabled$$.next(isDisabled);
        if (this._controlType$$.getValue() === 'single') {
            const isRequired = this.singleDateFormControl.hasValidator(Validators.required);
            this.isRequired$$.next(isRequired && !isDisabled);
        }
        if (this._controlType$$.getValue() === 'range') {
            const isRequired = this.rangeFormGroup.hasValidator(Validators.required);
            this.isRequired$$.next(isRequired && !isDisabled);
        }
    }
    _dateRangeRequired(rangeStartDate, rangeEndDate) {
        return () => {
            if (isNullOrEmpty(rangeStartDate.nativeElement.value) || isNullOrEmpty(rangeEndDate.nativeElement.value)) {
                return { requiredRange: true };
            }
            return null;
        };
    }
}
FFDatePickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFDatePickerComponent, deps: [{ token: INJECTOR }, { token: i1.LanguagesQuery }, { token: i2.DateAdapter }, { token: i3.FFMaterialControlsConfigService }], target: i0.ɵɵFactoryTarget.Component });
FFDatePickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FFDatePickerComponent, selector: "ff-date-picker", inputs: { singleLabel: "singleLabel", fromDateLabel: "fromDateLabel", toDateLabel: "toDateLabel", fromDateName: "fromDateName", toDateName: "toDateName", singleName: "singleName", placeholder: "placeholder", className: "className", minAge: "minAge", loaderDiameter: "loaderDiameter", tabIndex: "tabIndex", clearable: "clearable", hideRequiredMarker: "hideRequiredMarker", controlType: "controlType", customErrorMessages: "customErrorMessages", startDate: "startDate", startView: "startView", isShowLoading: "isShowLoading", dataType: "dataType" }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => FFDatePickerComponent)
        }
    ], viewQueries: [{ propertyName: "_rangeStartDate", first: true, predicate: ["rangeStartDate"], descendants: true }, { propertyName: "_rangeEndDate", first: true, predicate: ["rangeEndDate"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"controlType === 'range'; else single\">\n  <mat-form-field\n    class=\"w-100\"\n    [class]=\"className\"\n    [appearance]=\"appearance\"\n    [floatLabel]=\"floatLabel\"\n    [hideRequiredMarker]=\"hideRequiredMarker\"\n    [tabIndex]=\"tabIndex\"\n  >\n    <mat-label>{{ labelDateRange$$ | async }}</mat-label>\n\n    <mat-date-range-input [rangePicker]=\"picker\" [formGroup]=\"rangeFormGroup\" [dateFilter]=\"datepickerFilter\">\n      <input\n        #rangeStartDate\n        matStartDate\n        ffMaskDate\n        formControlName=\"dateFrom\"\n        [placeholder]=\"placeholder\"\n        [name]=\"fromDateControlName\"\n        [autocomplete]=\"fromDateControlName\"\n        (keyup)=\"onRangeStartChange()\"\n      />\n\n      <input\n        #rangeEndDate\n        matEndDate\n        ffMaskDate\n        formControlName=\"dateTo\"\n        [placeholder]=\"placeholder\"\n        [name]=\"toDateControlName\"\n        [autocomplete]=\"toDateControlName\"\n        (keyup)=\"onRangeEndChange()\"\n      />\n\n      <button class=\"clear\" *ngIf=\"clearable\" matSuffix mat-icon-button aria-label=\"Clear\" (click)=\"clearValue()\">\n        <mat-icon matSuffix>\n          <i class=\"ff-icon ff-close icon-size-5 cursor-pointer\"></i>\n        </mat-icon>\n      </button>\n\n      <span *ngIf=\"isShowLoading\" matSuffix class=\"mat-date-picker__loader-wrapper\">\n        <mat-spinner color=\"primary\" [diameter]=\"loaderDiameter\"></mat-spinner>\n      </span>\n    </mat-date-range-input>\n\n    <mat-datepicker-toggle matSuffix [for]=\"picker\"></mat-datepicker-toggle>\n    <mat-date-range-picker #picker></mat-date-range-picker>\n\n    <mat-error>\n      <ff-localized-mat-error\n        showErrorType=\"single\"\n        [formControlRef]=\"parentControl\"\n        [label]=\"fromDateLabel\"\n        [errorMessages]=\"customErrorMessages\"\n      ></ff-localized-mat-error>\n    </mat-error>\n  </mat-form-field>\n</ng-container>\n\n<ng-template #single>\n  <mat-form-field\n    class=\"w-100\"\n    [class]=\"className\"\n    [appearance]=\"appearance\"\n    [floatLabel]=\"floatLabel\"\n    [tabIndex]=\"tabIndex\"\n    [hideRequiredMarker]=\"hideRequiredMarker\"\n  >\n    <mat-label>{{ singleLabel }}</mat-label>\n    <input\n      matInput\n      ffMaskDate\n      [required]=\"isRequired$$ | async\"\n      [matDatepicker]=\"picker\"\n      (keyup)=\"inputChange($event)\"\n      [matDatepickerFilter]=\"datepickerFilter\"\n      [formControl]=\"singleDateFormControl\"\n      [placeholder]=\"placeholder\"\n      [name]=\"singleControlName\"\n      [autocomplete]=\"singleControlName\"\n    />\n\n    <button class=\"clear\" *ngIf=\"clearable\" matSuffix mat-icon-button aria-label=\"Clear\" (click)=\"clearValue()\">\n      <mat-icon matSuffix>\n        <i class=\"ff-icon ff-close icon-size-5 cursor-pointer\"></i>\n      </mat-icon>\n    </button>\n\n    <span *ngIf=\"isShowLoading\" matSuffix class=\"mat-date-picker__loader-wrapper\">\n      <mat-spinner color=\"primary\" [diameter]=\"loaderDiameter\"></mat-spinner>\n    </span>\n\n    <mat-error>\n      <ff-localized-mat-error\n        showErrorType=\"single\"\n        [formControlRef]=\"singleDateFormControl\"\n        [label]=\"singleLabel\"\n        [errorMessages]=\"customErrorMessages\"\n      ></ff-localized-mat-error>\n    </mat-error>\n\n    <mat-datepicker-toggle matSuffix [for]=\"picker\"></mat-datepicker-toggle>\n    <mat-datepicker [startAt]=\"startDate$$ | async\" [startView]=\"startView\" #picker> </mat-datepicker>\n  </mat-form-field>\n</ng-template>\n", components: [{ type: i4.MatFormField, selector: "mat-form-field", inputs: ["color", "appearance", "hideRequiredMarker", "hintLabel", "floatLabel"], exportAs: ["matFormField"] }, { type: i5.MatDateRangeInput, selector: "mat-date-range-input", inputs: ["rangePicker", "required", "dateFilter", "min", "max", "disabled", "separator", "comparisonStart", "comparisonEnd"], exportAs: ["matDateRangeInput"] }, { type: i6.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { type: i7.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { type: i8.MatProgressSpinner, selector: "mat-progress-spinner, mat-spinner", inputs: ["color", "diameter", "strokeWidth", "mode", "value"], exportAs: ["matProgressSpinner"] }, { type: i5.MatDatepickerToggle, selector: "mat-datepicker-toggle", inputs: ["for", "tabIndex", "aria-label", "disabled", "disableRipple"], exportAs: ["matDatepickerToggle"] }, { type: i5.MatDateRangePicker, selector: "mat-date-range-picker", exportAs: ["matDateRangePicker"] }, { type: i9.FFLocalizedMatErrorComponent, selector: "ff-localized-mat-error", inputs: ["formControlRef", "errorMessages", "label", "updateValidityDependencies", "showErrorType"], outputs: ["calculatedErrors"] }, { type: i5.MatDatepicker, selector: "mat-datepicker", exportAs: ["matDatepicker"] }], directives: [{ type: i10.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i4.MatLabel, selector: "mat-label" }, { type: i11.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { type: i11.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { type: i5.MatStartDate, selector: "input[matStartDate]", inputs: ["errorStateMatcher"], outputs: ["dateChange", "dateInput"] }, { type: i11.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: FFMaskDateDirective, selector: "[ffMaskDate]" }, { type: i11.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i11.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { type: i5.MatEndDate, selector: "input[matEndDate]", inputs: ["errorStateMatcher"], outputs: ["dateChange", "dateInput"] }, { type: i4.MatSuffix, selector: "[matSuffix]" }, { type: i4.MatError, selector: "mat-error", inputs: ["id"] }, { type: i13.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { type: i5.MatDatepickerInput, selector: "input[matDatepicker]", inputs: ["matDatepicker", "min", "max", "matDatepickerFilter"], exportAs: ["matDatepickerInput"] }, { type: i11.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { type: i11.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }], pipes: { "async": i10.AsyncPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFDatePickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ff-date-picker', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => FFDatePickerComponent)
                        }
                    ], template: "<ng-container *ngIf=\"controlType === 'range'; else single\">\n  <mat-form-field\n    class=\"w-100\"\n    [class]=\"className\"\n    [appearance]=\"appearance\"\n    [floatLabel]=\"floatLabel\"\n    [hideRequiredMarker]=\"hideRequiredMarker\"\n    [tabIndex]=\"tabIndex\"\n  >\n    <mat-label>{{ labelDateRange$$ | async }}</mat-label>\n\n    <mat-date-range-input [rangePicker]=\"picker\" [formGroup]=\"rangeFormGroup\" [dateFilter]=\"datepickerFilter\">\n      <input\n        #rangeStartDate\n        matStartDate\n        ffMaskDate\n        formControlName=\"dateFrom\"\n        [placeholder]=\"placeholder\"\n        [name]=\"fromDateControlName\"\n        [autocomplete]=\"fromDateControlName\"\n        (keyup)=\"onRangeStartChange()\"\n      />\n\n      <input\n        #rangeEndDate\n        matEndDate\n        ffMaskDate\n        formControlName=\"dateTo\"\n        [placeholder]=\"placeholder\"\n        [name]=\"toDateControlName\"\n        [autocomplete]=\"toDateControlName\"\n        (keyup)=\"onRangeEndChange()\"\n      />\n\n      <button class=\"clear\" *ngIf=\"clearable\" matSuffix mat-icon-button aria-label=\"Clear\" (click)=\"clearValue()\">\n        <mat-icon matSuffix>\n          <i class=\"ff-icon ff-close icon-size-5 cursor-pointer\"></i>\n        </mat-icon>\n      </button>\n\n      <span *ngIf=\"isShowLoading\" matSuffix class=\"mat-date-picker__loader-wrapper\">\n        <mat-spinner color=\"primary\" [diameter]=\"loaderDiameter\"></mat-spinner>\n      </span>\n    </mat-date-range-input>\n\n    <mat-datepicker-toggle matSuffix [for]=\"picker\"></mat-datepicker-toggle>\n    <mat-date-range-picker #picker></mat-date-range-picker>\n\n    <mat-error>\n      <ff-localized-mat-error\n        showErrorType=\"single\"\n        [formControlRef]=\"parentControl\"\n        [label]=\"fromDateLabel\"\n        [errorMessages]=\"customErrorMessages\"\n      ></ff-localized-mat-error>\n    </mat-error>\n  </mat-form-field>\n</ng-container>\n\n<ng-template #single>\n  <mat-form-field\n    class=\"w-100\"\n    [class]=\"className\"\n    [appearance]=\"appearance\"\n    [floatLabel]=\"floatLabel\"\n    [tabIndex]=\"tabIndex\"\n    [hideRequiredMarker]=\"hideRequiredMarker\"\n  >\n    <mat-label>{{ singleLabel }}</mat-label>\n    <input\n      matInput\n      ffMaskDate\n      [required]=\"isRequired$$ | async\"\n      [matDatepicker]=\"picker\"\n      (keyup)=\"inputChange($event)\"\n      [matDatepickerFilter]=\"datepickerFilter\"\n      [formControl]=\"singleDateFormControl\"\n      [placeholder]=\"placeholder\"\n      [name]=\"singleControlName\"\n      [autocomplete]=\"singleControlName\"\n    />\n\n    <button class=\"clear\" *ngIf=\"clearable\" matSuffix mat-icon-button aria-label=\"Clear\" (click)=\"clearValue()\">\n      <mat-icon matSuffix>\n        <i class=\"ff-icon ff-close icon-size-5 cursor-pointer\"></i>\n      </mat-icon>\n    </button>\n\n    <span *ngIf=\"isShowLoading\" matSuffix class=\"mat-date-picker__loader-wrapper\">\n      <mat-spinner color=\"primary\" [diameter]=\"loaderDiameter\"></mat-spinner>\n    </span>\n\n    <mat-error>\n      <ff-localized-mat-error\n        showErrorType=\"single\"\n        [formControlRef]=\"singleDateFormControl\"\n        [label]=\"singleLabel\"\n        [errorMessages]=\"customErrorMessages\"\n      ></ff-localized-mat-error>\n    </mat-error>\n\n    <mat-datepicker-toggle matSuffix [for]=\"picker\"></mat-datepicker-toggle>\n    <mat-datepicker [startAt]=\"startDate$$ | async\" [startView]=\"startView\" #picker> </mat-datepicker>\n  </mat-form-field>\n</ng-template>\n" }]
        }], ctorParameters: function () {
        return [{ type: i0.Injector, decorators: [{
                        type: Inject,
                        args: [INJECTOR]
                    }] }, { type: i1.LanguagesQuery }, { type: i2.DateAdapter }, { type: i3.FFMaterialControlsConfigService }];
    }, propDecorators: { singleLabel: [{
                type: Input
            }], fromDateLabel: [{
                type: Input
            }], toDateLabel: [{
                type: Input
            }], fromDateName: [{
                type: Input
            }], toDateName: [{
                type: Input
            }], singleName: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], className: [{
                type: Input
            }], minAge: [{
                type: Input
            }], loaderDiameter: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], clearable: [{
                type: Input
            }], hideRequiredMarker: [{
                type: Input
            }], controlType: [{
                type: Input
            }], customErrorMessages: [{
                type: Input
            }], startDate: [{
                type: Input
            }], startView: [{
                type: Input
            }], isShowLoading: [{
                type: Input
            }], dataType: [{
                type: Input
            }], _rangeStartDate: [{
                type: ViewChild,
                args: ['rangeStartDate']
            }], _rangeEndDate: [{
                type: ViewChild,
                args: ['rangeEndDate']
            }] } });

class FFDatePickerModule {
}
FFDatePickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFDatePickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FFDatePickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFDatePickerModule, declarations: [FFDatePickerComponent, FFMaskDateDirective], imports: [CommonModule,
        MatFormFieldModule,
        MatDatepickerModule,
        LocalizationModule,
        MatInputModule,
        MatButtonModule,
        FFForceUppercaseModule,
        FFLocalizedMatErrorModule,
        FFRegexFilterModule,
        MatIconModule,
        ReactiveFormsModule,
        MatProgressSpinnerModule], exports: [FFDatePickerComponent, FFMaskDateDirective] });
FFDatePickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFDatePickerModule, providers: [], imports: [[
            CommonModule,
            MatFormFieldModule,
            MatDatepickerModule,
            LocalizationModule,
            MatInputModule,
            MatButtonModule,
            FFForceUppercaseModule,
            FFLocalizedMatErrorModule,
            FFRegexFilterModule,
            MatIconModule,
            ReactiveFormsModule,
            MatProgressSpinnerModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FFDatePickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatFormFieldModule,
                        MatDatepickerModule,
                        LocalizationModule,
                        MatInputModule,
                        MatButtonModule,
                        FFForceUppercaseModule,
                        FFLocalizedMatErrorModule,
                        FFRegexFilterModule,
                        MatIconModule,
                        ReactiveFormsModule,
                        MatProgressSpinnerModule
                    ],
                    exports: [FFDatePickerComponent, FFMaskDateDirective],
                    declarations: [FFDatePickerComponent, FFMaskDateDirective],
                    providers: []
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FFDatePickerComponent, FFDatePickerModule, FFMaskDateDirective };
